import { dateToWallDate } from "./datetime";

/** Inclusive range of Prague calendar days, both `YYYY-MM-DD`. */
export type DateRange = { from: string; to: string };

export const DATE_RANGE_PRESETS = [
  "today",
  "yesterday",
  "thisWeek",
  "lastWeek",
  "pastTwoWeeks",
  "thisMonth",
  "lastMonth",
  "thisYear",
  "lastYear",
] as const;
export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number];

function parse(day: string): Date {
  return new Date(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)));
}

export function addDays(day: string, n: number): string {
  const d = parse(day);
  d.setDate(d.getDate() + n);
  return dateToWallDate(d);
}

function monthRange(year: number, month: number, months = 1): DateRange {
  return {
    from: dateToWallDate(new Date(year, month, 1)),
    to: dateToWallDate(new Date(year, month + months, 0)),
  };
}

/** Monday of the week `day` falls in. */
function weekStart(day: string): string {
  return addDays(day, -((parse(day).getDay() + 6) % 7));
}

export function presetRange(preset: DateRangePreset, today: string): DateRange {
  const d = parse(today);
  const y = d.getFullYear();
  const m = d.getMonth();
  const monday = weekStart(today);
  switch (preset) {
    case "today":
      return { from: today, to: today };
    case "yesterday": {
      const y1 = addDays(today, -1);
      return { from: y1, to: y1 };
    }
    case "thisWeek":
      return { from: monday, to: addDays(monday, 6) };
    case "lastWeek":
      return { from: addDays(monday, -7), to: addDays(monday, -1) };
    case "pastTwoWeeks":
      return { from: addDays(monday, -7), to: addDays(monday, 6) };
    case "thisMonth":
      return monthRange(y, m);
    case "lastMonth":
      return monthRange(y, m - 1);
    case "thisYear":
      return monthRange(y, 0, 12);
    case "lastYear":
      return monthRange(y - 1, 0, 12);
  }
}

export function matchingPreset(range: DateRange | null, today: string): DateRangePreset | null {
  if (!range) return null;
  return (
    DATE_RANGE_PRESETS.find((p) => {
      const r = presetRange(p, today);
      return r.from === range.from && r.to === range.to;
    }) ?? null
  );
}

/**
 * The neighbouring period of the same shape: whole months move by their
 * month count (so "October" steps to "November", not by 31 days), anything
 * else by its length in days.
 */
export function shiftRange(range: DateRange, direction: 1 | -1): DateRange {
  const from = parse(range.from);
  const to = parse(range.to);
  const wholeMonths = from.getDate() === 1 && addDays(range.to, 1).slice(8, 10) === "01";
  if (wholeMonths) {
    const months =
      (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth() + 1;
    return monthRange(from.getFullYear(), from.getMonth() + direction * months, months);
  }
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  return { from: addDays(range.from, direction * days), to: addDays(range.to, direction * days) };
}
